import React, { useEffect, useMemo, useRef, useState } from 'react';
import { displayNombre, formatCOP, imageUrl } from '../services/api';
import CachedProductImage from './CachedProductImage';
import ImageZoomLightbox from './ImageZoomLightbox';
import {
  allOpcionesAgotadas,
  firstDisponibleOpcion,
  needsOpcionPick,
  realOpciones,
} from '../utils/productoOpciones';

function productKey(p) {
  return String(p?.id ?? p?.codigo ?? '');
}

function cleanRef(value) {
  const t = String(value || '').trim();
  if (!t) return '';
  if (/^IOQ!?$/i.test(t)) return '';
  if (/^TALLA\s*\$$/i.test(t)) return 'TALLA S';
  if (/^0+$/.test(t)) return '';
  return t;
}

function displayCodigo(codigo) {
  const c = String(codigo || '').trim();
  if (!c || /^0+$/.test(c)) return '';
  return c.replace(/^0+(?=\d)/, '') || c;
}

function displayPresentacion(product) {
  const desc = String(product?.descripcion || '').trim();
  if (desc) return desc;
  const ref = cleanRef(product?.referencia);
  if (/^(TALLA|X\d|CAJA|UNIDAD|PAQUETE|ROLLO)/i.test(ref)) return ref;
  return '';
}

function isSinStock(product) {
  if (!product) return true;
  if (product.disponibilidad === 'sin_stock') return true;
  if (product.en_stock != null && Number(product.en_stock) === 0) return true;
  if (product.stock != null && Number(product.stock) === 0) return true;
  return product.cantidad != null && Number(product.cantidad) <= 0;
}

export default function ProductModal({ product, products = [], onClose, onAdd, onSelect }) {
  const [qty, setQty] = useState(1);
  const [observaciones, setObservaciones] = useState('');
  const [opcionId, setOpcionId] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, active: false });

  const list = useMemo(() => {
    if (Array.isArray(products) && products.length) return products;
    return product ? [product] : [];
  }, [products, product]);

  const index = useMemo(() => {
    if (!product) return -1;
    const key = productKey(product);
    const i = list.findIndex((p) => productKey(p) === key);
    return i >= 0 ? i : 0;
  }, [list, product]);

  const current = list[index] || product;
  const canNav = list.length > 1;
  const hasPrev = canNav && index > 0;
  const hasNext = canNav && index < list.length - 1;

  const ops = realOpciones(current);
  const pickOpcion = needsOpcionPick(current);
  const selectedOp = ops.find((o) => o.id === opcionId) || firstDisponibleOpcion(current);

  useEffect(() => {
    setQty(1);
    setObservaciones('');
    setZoomOpen(false);
    setOpcionId(firstDisponibleOpcion(current)?.id || null);
  }, [current?.codigo, current?.id]);

  useEffect(() => {
    function onKey(e) {
      if (zoomOpen) return;
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault();
        onSelect?.(list[index - 1]);
      }
      if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        onSelect?.(list[index + 1]);
      }
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPrev, hasNext, index, list, onSelect, onClose, zoomOpen]);

  if (!current) return null;

  const img = imageUrl(selectedOp?.imagen || current.imagen);
  const name = displayNombre(current) || `Producto ${current.codigo}`;
  const codigo = displayCodigo(current.codigo);
  const referencia = cleanRef(current.referencia);
  const refVisible =
    referencia && referencia !== String(current.codigo || '').trim() && referencia !== codigo;
  const presentacion = displayPresentacion(current);
  const opcionAgotada = Boolean(selectedOp && !selectedOp.disponible);
  const sinStock =
    isSinStock(current) || allOpcionesAgotadas(current) || (pickOpcion && opcionAgotada);
  const canAdd = !sinStock && (!pickOpcion || (selectedOp && selectedOp.disponible));

  function go(delta) {
    const next = list[index + delta];
    if (next) onSelect?.(next);
  }

  function onTouchStart(e) {
    if (zoomOpen) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  }

  function onTouchEnd(e) {
    if (!touchRef.current.active || !canNav || zoomOpen) return;
    const t = e.changedTouches?.[0];
    touchRef.current.active = false;
    if (!t) return;
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && hasNext) go(1);
    if (dx > 0 && hasPrev) go(-1);
  }

  const meta = [
    codigo ? `Código: ${codigo}` : null,
    refVisible ? `Ref: ${referencia}` : null,
    presentacion || null,
  ].filter(Boolean);

  return (
    <>
      <div className="modal-overlay" onClick={zoomOpen ? undefined : onClose} role="presentation">
        <div
          className="product-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={zoomOpen}
          >
            ✕
          </button>
          <div className="product-modal-scroll">
            <div
              className="product-modal-img"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
            >
              {img ? (
                <button
                  type="button"
                  className="product-modal-img-btn"
                  onClick={() => setZoomOpen(true)}
                  aria-label="Ampliar foto"
                >
                  <CachedProductImage src={img} alt={name} loading="eager" fetchPriority="high" />
                  <span className="product-zoom-badge">Ampliar</span>
                </button>
              ) : (
                <div className="product-modal-noimg">Sin imagen</div>
              )}

              {canNav ? (
                <>
                  <button
                    type="button"
                    className="product-gallery-nav prev"
                    onClick={() => go(-1)}
                    disabled={!hasPrev}
                    aria-label="Producto anterior"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="product-gallery-nav next"
                    onClick={() => go(1)}
                    disabled={!hasNext}
                    aria-label="Producto siguiente"
                  >
                    ›
                  </button>
                  <span className="product-gallery-count">
                    {index + 1} / {list.length}
                  </span>
                </>
              ) : null}
            </div>
            <div className="product-modal-body">
              {current.subcategoria ? <span className="tag">{current.subcategoria}</span> : null}
              <h2>{name}</h2>
              {sinStock ? <p className="product-stock is-oos">Agotado</p> : null}
              {meta.length ? (
                <p className="product-meta">
                  {meta.map((line, i) => (
                    <span key={line}>
                      {i > 0 ? <span className="product-meta-sep"> · </span> : null}
                      {line}
                    </span>
                  ))}
                </p>
              ) : null}
              <p className="product-price">{formatCOP(current.precio)}</p>

              {pickOpcion ? (
                <div className="product-modal-opciones">
                  <span>Elige opción / sabor</span>
                  <div className="product-modal-opciones-grid">
                    {ops.map((op) => {
                      const src = imageUrl(op.imagen);
                      const on = selectedOp?.id === op.id;
                      return (
                        <button
                          key={op.id}
                          type="button"
                          className={`product-modal-opcion${on ? ' is-on' : ''}${
                            op.disponible ? '' : ' is-agotada'
                          }`}
                          onClick={() => setOpcionId(op.id)}
                        >
                          {src ? (
                            <CachedProductImage src={src} alt="" loading="lazy" />
                          ) : (
                            <span className="product-modal-opcion-ph" />
                          )}
                          <em>{op.nombre || 'Opción'}</em>
                          {!op.disponible ? <strong>Agotada</strong> : null}
                        </button>
                      );
                    })}
                  </div>
                  {pickOpcion && selectedOp && !selectedOp.disponible ? (
                    <p className="product-modal-opcion-warn">Esa opción está agotada. Elige otra.</p>
                  ) : null}
                </div>
              ) : null}

              <div className="qty-row">
                <span>Cantidad</span>
                <div className="qty-stepper">
                  <button
                    type="button"
                    disabled={!canAdd}
                    onClick={() => setQty((n) => Math.max(1, n - 1))}
                  >
                    −
                  </button>
                  <strong>{qty}</strong>
                  <button type="button" disabled={!canAdd} onClick={() => setQty((n) => n + 1)}>
                    +
                  </button>
                </div>
              </div>
              <label className="product-obs-field">
                <span>Observaciones</span>
                <textarea
                  rows={2}
                  placeholder="Color, talla, presentación…"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </label>
            </div>
          </div>
          <div className="product-modal-foot">
            <button
              type="button"
              className="btn-primary"
              disabled={!canAdd}
              onClick={() => {
                if (!canAdd) return;
                onAdd(current, {
                  cantidad: qty,
                  observaciones: observaciones.trim(),
                  opcion_id: selectedOp?.id || null,
                  opcion_nombre: selectedOp?.nombre || null,
                  imagen: selectedOp?.imagen || current.imagen,
                });
              }}
            >
              {!canAdd
                ? pickOpcion && selectedOp && !selectedOp.disponible
                  ? 'Opción agotada'
                  : 'Producto agotado'
                : `Añadir al carrito · ${formatCOP(current.precio * qty)}`}
            </button>
          </div>
        </div>
      </div>
      {zoomOpen && img ? (
        <ImageZoomLightbox src={img} alt={name} onClose={() => setZoomOpen(false)} />
      ) : null}
    </>
  );
}
